import { Injectable, inject } from '@angular/core';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { Asset, InstrumentType } from '@zero/shared/util-models';
import { catchError, of, tap } from 'rxjs';
import { MinimumQuantitySurcharge } from '../../entities/minimum-quantity-surcharge.entity';

@Injectable({
  providedIn: 'root',
})
export class MinimumQuantitySurchargeService {
  readonly #showLowTradingHintBuffer = 20;
  readonly #disproportionateFeesThreshold = 20;

  readonly #customerStateFacade = inject(CustomerStateFacade);

  public determine = (orderValue: number, quantity: number, asset: Asset): Promise<MinimumQuantitySurcharge> => {
    return new Promise<MinimumQuantitySurcharge>((resolve, reject) => {
      if (this.#customerStateFacade.customerBaseData) {
        resolve(this.getMinimumQuantitySurcharge(orderValue, quantity, asset));
        return;
      }

      this.#customerStateFacade.loadCustomerBaseData().pipe(
        tap(() => resolve(this.getMinimumQuantitySurcharge(orderValue, quantity, asset))),
        catchError((e) => {
          reject(e);
          return of();
        }),
      );
    });
  };

  public getMinimumQuantitySurcharge = (orderValue: number, quantity: number, asset: Asset): MinimumQuantitySurcharge => {
    const { customerBaseData } = this.#customerStateFacade;

    if (asset.instrumentType === InstrumentType.Derivative) {
      const shouldPay = Boolean(asset.lowTradingThresholdQuantity) && quantity < asset.lowTradingThresholdQuantity;
      return {
        value: shouldPay ? asset.tradingFees?.minimumQuantitySurcharge : 0,
        threshold: asset.lowTradingThresholdQuantity,
        showLowTradingHint: false,
        showDisproportionateFeesHint: shouldPay && orderValue < this.#disproportionateFeesThreshold,
      };
    }

    const shouldPay = Boolean(asset.lowTradingThreshold) && orderValue < asset.lowTradingThreshold;
    const maybePay = Boolean(asset.lowTradingThreshold) && orderValue < asset.lowTradingThreshold + this.#showLowTradingHintBuffer;
    return {
      value: shouldPay ? asset.tradingFees?.minimumQuantitySurcharge : 0,
      threshold: asset.lowTradingThreshold,
      showLowTradingHint: !customerBaseData?.optedInLowTrading && maybePay,
      showDisproportionateFeesHint: shouldPay && orderValue < this.#disproportionateFeesThreshold,
    };
  };
}
